<template>
  <section class="c-section">
    <v-product-image
      v-if="$section.resource.logo && show"
      class="c-section__logo"
      :product="$section.resource"
      :type="ImageType.LOGO"
    ></v-product-image>

    <div
      v-if="show"
      class="c-section__header"
    >
      <slot name="headerLeft">
        <component
          :is="$section.isUpsellSection ? 'p' : 'h2'"
          class="c-section__header-main"
        >
          <v-badge
            :product="$section.resource"
            class="c-highlight__badge"
          ></v-badge>
          <span
            v-dompurify-html="$section.sectionHeader"
            class="c-section__title"
          ></span>
          <span
            v-if="$section.resource.lead"
            class="c-section__header-additional"
          >
            {{ $section.resource.lead }}
          </span>
          <view-all-link
            v-if="extraElements"
            :path="path"
            :label="viewAllLabel"
            :section-route="sectionRoute"
          ></view-all-link>
        </component>
      </slot>
      <slot name="headerRight">
        <ui-indicator
          :total="totalTileColumns"
          :current="sliderIndex"
          :per-page="visibleSlideSize"
        ></ui-indicator>
      </slot>
    </div>

    <v-ad-section
      ref="ad"
      class="c-section__ad-container"
      :class="{ 'is-mobile': showMobileAd }"
      @isEmpty="setAdIsEmpty"
    ></v-ad-section>

    <div
      v-if="show"
      ref="slider"
      class="c-section__container js-section-container"
      :class="{ 'has-ad': !showMobileAd && !adIsEmpty }"
    >
      <div
        ref="slider-wrapper"
        class="c-section__wrapper"
        :style="wrapperStyle"
        @transitionend.self="updateSliderState"
      >
        <slot :products="visibleProducts"></slot>
        <view-all-link
          v-if="extraElements"
          :path="path"
          :label="viewAllLabel"
          :section-route="sectionRoute"
          is-tile
        ></view-all-link>
      </div>

      <slot
        v-if="showControlsOnMobile || !$window.isMobileDevice"
        name="controls"
      >
        <div
          class="c-section__control c-section__control--prev"
          :class="{
            'is-active': hasPrev,
            'has-ad': !showMobileAd && !adIsEmpty,
          }"
          :style="{ color: $section.sectionStyle.color }"
          @click.prevent="prev"
        >
          <div
            class="c-section__control-background"
            :style="{ background: $section.sectionStyle.background }"
          ></div>
          <i class="o-icon o-icon--left"></i>
        </div>
        <div
          class="c-section__control c-section__control--next"
          :class="{ 'is-active': hasNext }"
          :style="{ color: $section.sectionStyle.color }"
          @click.prevent="next"
        >
          <div
            class="c-section__control-background"
            :style="{ background: $section.sectionStyle.background }"
          ></div>
          <i class="o-icon o-icon--right"></i>
        </div>
      </slot>
    </div>
  </section>
</template>
<script>
  import { EventType, ImageType, RouteName, TileLayout } from '@/shared/enums';
  import { expandTileMixin, viewportMixin } from '@/index/shared/mixins';
  import { VAdSection, VBadge } from '@/index/shared/components';
  import VProductImage from '../product-image/index.vue';
  import UiIndicator from './indicator.vue';
  import ViewAllLink from './view-all-link.vue';
  import slideSectionMixin from './mixins/slider';

  export default {
    components: {
      UiIndicator,
      VProductImage,
      VAdSection,
      VBadge,
      ViewAllLink,
    },
    enums: {
      RouteName,
      ImageType,
    },
    mixins: [
      expandTileMixin,
      slideSectionMixin,
      viewportMixin,
    ],
    props: {
      scale: {
        type: Object,
        default: () => ({ x: 2, y: 2, z: 2 }),
      },
      lines: {
        type: Number,
        default: 1,
      },
      layout: {
        type: String,
        default: TileLayout.DEFAULT,
      },
      center: {
        type: Boolean,
        default: false,
      },
      showControlsOnMobile: Boolean,
      hideFirstElement: Boolean,
    },
    data() {
      return {
        vTile: undefined,
        show: false,
        adIsEmpty: true,
        showMobileAd: this.$window.isSmallAdView,
        extraElements: 0,
      };
    },
    computed: {
      viewAllLabel() {
        return this.$section.resource.viewAllLabel;
      },
      path() {
        return this.$section.resource.urlWeb && this.$section.resource.urlWeb.replace(/\${(FO_URL|FO_DEEPLINK_URL)}/, '');
      },
      totalTileColumns() {
        const elements = this.hideFirstElement ? this.products.length - 1 : this.products.length;
        return elements / this.lines;
      },
      additionalItem() {
        return !this.adIsEmpty && !this.$window.isSmallAdView && !this.showMobileAd;
      },
      sectionRoute() {
        if (!this.$section.resource.catalog || !this.$section.resource.id) {
          return;
        }

        return {
          name: RouteName.DETAIL_SECTION,
          params: {
            label: this.$route.meta.label || this.$route.params.label,
            id: this.$section.resource.id,
            slug: this.$section.resource.slug || '_',
          },
        };
      },
    },
    watch: {
      products() {
        this.slideToInitialIndex();
      },
    },
    mounted() {
      this.extraElements = Number(!!this.path || !!this.sectionRoute);
      this.observe();
    },
    beforeDestroy() {
      this.$EventBus.off(EventType.RESIZE, this.updateSlider);
      this.detachSlider();
    },
    methods: {
      setSectionWidth() {
        if (!this.$refs.slider) {
          return;
        }

        const sectionWidth = this.$refs.slider.getBoundingClientRect().width;
        this.$el.style.setProperty('--section-width', `${sectionWidth}px`);
      },
      updateSlider() {
        this.setSectionWidth();
        this.updateSlideSize();
        this.updateSliderArrows();
      },
      onViewportEnter() {
        this.show = true;
        this.unobserve();
        this.extraElements = Number(!!this.path || !!this.sectionRoute);
        this.$nextTick(() => {
          this.attachSlider();
          this.$EventBus.on(EventType.RESIZE, this.updateSlider);
        });
      },
      setAdIsEmpty(value) {
        this.adIsEmpty = value;
        this.$nextTick(() => this.updateSlider());
      },
    },
  };
</script>
